import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ChefHat, Eye, EyeOff, Loader2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

export const LoginPage = () => {
    const { login } = useAuth();
    const navigate = useNavigate();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!email || !password) {
            toast.error('Veuillez remplir tous les champs');
            return;
        }
        
        setLoading(true);
        try {
            await login(email, password);
            toast.success('Connexion réussie!');
            navigate('/');
        } catch (error) {
            console.error('Login error:', error);
            toast.error(error.response?.data?.detail || 'Erreur de connexion');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center py-12 px-4 relative overflow-hidden">
            {/* Decorative blobs */}
            <div className="blob-green top-10 -left-20 opacity-30" />
            <div className="blob-purple bottom-10 -right-20 opacity-30" />
            <div className="blob-orange top-1/2 right-10 opacity-20" />
            
            <div className="w-full max-w-md relative z-10">
                {/* Logo */}
                <Link to="/" className="flex items-center justify-center gap-3 mb-8 group">
                    <div className="p-3 bg-gradient-cameleon rounded-2xl border-2 border-foreground shadow-pop group-hover:shadow-pop-hover transition-all">
                        <ChefHat className="w-8 h-8 text-white" />
                    </div>
                    <div>
                        <span className="font-heading font-black text-2xl block">Recettes</span>
                        <span className="font-heading font-black text-2xl text-gradient-cameleon -mt-1 block">Caméléon</span>
                    </div>
                </Link>
                
                {/* Card */}
                <div className="bg-white rounded-3xl border-2 border-foreground shadow-[8px_8px_0px_0px_#1e293b] p-8">
                    <div className="text-center mb-8">
                        <h1 className="font-heading text-3xl font-black text-foreground mb-2">Connexion</h1>
                        <p className="text-muted-foreground">
                            Accédez à votre compte pour sauvegarder vos recettes
                        </p>
                    </div>
                    
                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-bold">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="votre@email.com"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="rounded-xl border-2 border-foreground py-6 focus:shadow-pop transition-shadow"
                                data-testid="login-email-input"
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <Label htmlFor="password" className="font-bold">Mot de passe</Label>
                                <Link 
                                    to="/forgot-password" 
                                    className="text-sm text-primary hover:underline"
                                    data-testid="forgot-password-link"
                                >
                                    Mot de passe oublié?
                                </Link>
                            </div>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="rounded-xl border-2 border-foreground py-6 pr-12 focus:shadow-pop transition-shadow"
                                    data-testid="login-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>
                        
                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-pop bg-primary text-white py-6 text-lg"
                            data-testid="login-submit-btn"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                    Connexion...
                                </>
                            ) : (
                                <>
                                    Se connecter
                                    <ArrowRight className="w-5 h-5 ml-2" />
                                </>
                            )}
                        </Button>
                    </form>
                    
                    <div className="mt-8 text-center">
                        <p className="text-muted-foreground">
                            Pas encore de compte?{' '}
                            <Link 
                                to="/register" 
                                className="text-primary font-bold hover:underline" 
                                data-testid="register-link"
                            >
                                S'inscrire
                            </Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
